import { PLAN_HISTORY_FIELDS, type PlanChanges } from '@/lib/billing/plan-changes'
import { BILLING_CHANNELS } from '@/lib/billing/constants'
import { FIELD_LABELS, SECTIONS, type NumericField, type SectionId } from './catalog'
import { DEFAULT_PLAN_FORM } from './defaults'
import { NUMERIC_DEFINITIONS, fieldSection, toEditorAmount } from './model'
import type { PlanPayload } from './types'

export interface PlanChangeRow {
  field: string
  label: string
  section: SectionId
  from: string
  to: string
}

export interface PlanValueRow {
  field: string
  label: string
  section: SectionId
  value: string
  /** Saved, but without effect under the plan's current switches. */
  inactive: boolean
}

export function describePlanChanges(changes: PlanChanges): PlanChangeRow[] {
  return orderFields(Object.keys(changes)).map(field => ({
    field,
    label: fieldLabel(field),
    section: fieldSection(field),
    from: formatPlanValue(field, changes[field].from),
    to: formatPlanValue(field, changes[field].to),
  }))
}

export function describePlanValues(payload: PlanPayload): PlanValueRow[] {
  const values = payload as unknown as Record<string, unknown>
  return orderFields(PLAN_HISTORY_FIELDS.filter(field => field in values)).map(field => ({
    field,
    label: fieldLabel(field),
    section: fieldSection(field),
    value: formatPlanValue(field, values[field]),
    inactive: isInactive(field, payload),
  }))
}

export function groupBySection<T extends { section: SectionId }>(rows: T[]): { id: SectionId; title: string; rows: T[] }[] {
  return SECTIONS
    .map(section => ({ id: section.id, title: section.title, rows: rows.filter(row => row.section === section.id) }))
    .filter(group => group.rows.length > 0)
}

/** `720` → `30 days`. */
export function hoursAsDays(hours: number): string {
  const days = Math.round((hours / 24) * 10) / 10
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

/** `720` → `720 hours (30 days)`; under a day stays in hours. */
export function hoursWithDays(hours: number): string {
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  return `${hours} hours (${hoursAsDays(hours)})`
}

export function formatPlanTimestamp(value: string): string {
  return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** Exact values as the editor inputs show them: no credit rounding. */
export function formatPlanValue(field: string, value: unknown): string {
  const numeric = NUMERIC_DEFINITIONS.find(definition => definition.key === field)
  if (numeric) return formatNumeric(numeric, value)
  if (value === null || value === undefined || value === '') return 'Not set'
  if (typeof value === 'boolean') return value ? 'On' : 'Off'
  if (field === 'billing_intervals' && Array.isArray(value)) return value.length ? value.map(formatInterval).join('; ') : 'None'
  if (Array.isArray(value)) return value.length ? value.join('; ') : 'None'
  if (typeof value === 'string') return formatText(field, value)
  if (typeof value === 'object' && Object.keys(value).length === 0) return 'None'
  return JSON.stringify(value)
}

const FORM_ORDER = Object.keys(DEFAULT_PLAN_FORM)
const SECTION_ORDER = SECTIONS.map(section => section.id)

function orderFields(fields: string[]): string[] {
  return [...fields].sort((a, b) =>
    SECTION_ORDER.indexOf(fieldSection(a)) - SECTION_ORDER.indexOf(fieldSection(b))
    || rank(a) - rank(b))
}

function rank(field: string): number {
  const index = FORM_ORDER.indexOf(field)
  return index === -1 ? FORM_ORDER.length : index
}

function fieldLabel(field: string): string {
  const numeric = NUMERIC_DEFINITIONS.find(definition => definition.key === field)
  return numeric?.label ?? FIELD_LABELS[field as keyof typeof FIELD_LABELS] ?? field
}

function formatNumeric(field: NumericField, value: unknown): string {
  if (value === null || value === undefined) return field.empty ?? 'Not set'
  if (typeof value !== 'number') return String(value)
  const amount = toEditorAmount(value, field)
  // `1 credit`, `1 day`: the catalog units are plural.
  const text = field.unit ? `${amount} ${amount === '1' ? field.unit.replace(/^(credit|day)s\b/, '$1') : field.unit}` : amount
  if (value === 0 && field.zero) return `${text} (${field.zero})`
  return field.unit === 'hours' ? hoursWithDays(value) : text
}

function formatInterval(value: unknown): string {
  const { interval, interval_count: count = 1, discount_percent: discount = 0 } = value as { interval?: string; interval_count?: number; discount_percent?: number }
  const period = `${count} ${interval ?? 'period'}${count === 1 ? '' : 's'}`
  return discount ? `${period}, ${discount}% off` : period
}

function formatText(field: string, value: string): string {
  switch (field) {
    case 'status':
    case 'billing_channel':
      return value.charAt(0).toUpperCase() + value.slice(1)
    default:
      return value
  }
}

function isInactive(field: string, plan: PlanPayload): boolean {
  switch (field) {
    case 'voice_trial_duration_hours':
    case 'voice_trial_budget_microusd':
      return plan.voice_included || !plan.voice_trial_enabled
    case 'campaigns_limit':
    case 'campaigns_trial_enabled':
      return !plan.campaigns_enabled
    case 'shopify_plan_handle':
      return plan.billing_channel !== BILLING_CHANNELS.SHOPIFY
    case 'plan_credits_expire':
      return plan.payment_to_balance_microusd === 0
    default:
      return false
  }
}
